import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Button } from './ui/Button';

// First-run screen for a fleet with no machine (#6567, prototype `page()`'s
// `section.first`): "Connect your first machine", a lede, the three steps and
// the Connect button, in place of the Machines list. Presenter, prop-driven.
//
// Two prototype parts are left out on purpose: Step 1's recommended server size
// (never-ship, brief.md) and the "Read the setup guide" link.
//
// ONE BRAND BUTTON PER VIEW: on an empty fleet this button is the brand one and
// the header's "Connect a machine" renders `outline` (`FleetHeader`'s `firstRun`).
//
// WRITE ACTIONS ARE ADMIN-ONLY (owner ruling G21): a member or viewer reads the
// screen and gets no button — the server would refuse the mint (`write:runners`)
// and offering the door is the UX half of hiding it.

/** The three steps, each key spelled out so the locale guards see it used. */
const STEPS: readonly { readonly title: () => string; readonly desc: () => string }[] = [
  {
    title: () => t('fleet.connect.first_step1_title'),
    desc: () => t('fleet.connect.first_step1_desc'),
  },
  {
    title: () => t('fleet.connect.first_step2_title'),
    desc: () => t('fleet.connect.first_step2_desc'),
  },
  {
    title: () => t('fleet.connect.first_step3_title'),
    desc: () => t('fleet.connect.first_step3_desc'),
  },
];

export type FleetFirstRunProps = {
  /** `canAdminWrite` on a settled role: the caller may connect a machine. */
  readonly canWrite: boolean;
  readonly onConnect: () => void;
};

export function FleetFirstRun(props: FleetFirstRunProps): JSX.Element {
  return (
    <section class="fleet-first" aria-labelledby="fleet-first-heading">
      <div>
        <h2 id="fleet-first-heading" class="fleet-first__title">
          {t('fleet.connect.first_title')}
        </h2>
        <p class="fleet-first__lede">{t('fleet.connect.first_lede')}</p>
      </div>
      <ol class="fleet-first__steps">
        <For each={STEPS}>
          {(step, i) => (
            <li class="fleet-first__step">
              <span class="fleet-first__step-n">
                {t('fleet.connect.first_step', { n: i() + 1 })}
              </span>
              <h3 class="fleet-first__step-title">{step.title()}</h3>
              <p class="fleet-first__step-desc">{step.desc()}</p>
            </li>
          )}
        </For>
      </ol>
      <Show when={props.canWrite}>
        <div>
          <Button variant="brand" onClick={() => props.onConnect()}>
            {t('fleet.connect.first_run_cta')}
          </Button>
        </div>
      </Show>
    </section>
  );
}
